import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RepositoryBase } from '@asm/features/sync/repositories/repository-base';
import { A071Repository } from '@asm/features/sync/repositories/a071.repository';
import { A073Repository } from '@asm/features/sync/repositories/a073.repository';
import { A155Repository } from '@asm/features/sync/repositories/a155.repository';
import { T682IRepository } from '@asm/features/sync/repositories/t682i.repository';
import { T682ZRepository } from '@asm/features/sync/repositories/t682z.repository';
import { T682I, T682Z, T685 } from '@asm/features/sync/models/pricing-tables.model';
import { LocalConditionResult } from '@asm/features/sync/services/pricing/pricing.interfaces';

@Injectable({ providedIn: 'root' })
export class DefaultLocalPricingConditionsService {
  private readonly t682iRepo = inject(T682IRepository);
  private readonly t682zRepo = inject(T682ZRepository);
  private readonly a071Repo = inject(A071Repository);
  private readonly a073Repo = inject(A073Repository);
  private readonly a155Repo = inject(A155Repository);

  async getPricingConditions(
    context: Record<string, any>,
    conditions: T685[] = [],
  ): Promise<Map<string, LocalConditionResult>> {
    if (!conditions.length) {
      return new Map();
    }

    const automaticConditions = conditions.filter(condition => !!condition.KOZGF);
    if (!automaticConditions.length) {
      return new Map();
    }

    const [allSequences, allFields] = await Promise.all([
      firstValueFrom(this.t682iRepo.getAll()),
      firstValueFrom(this.t682zRepo.getAll()),
    ]);

    const results = new Map<string, LocalConditionResult>();
    const conditionFieldsBySequence = this.buildConditionFieldsBySequence(allFields);
    const sequencesByKey = this.buildSequencesByKey(allSequences);
    const tableDataCache = new Map<string, any[]>();

    for (const sequenceKey of Object.keys(sequencesByKey)) {
      const sequences = this.sortSequencesByHitProbability(sequencesByKey[sequenceKey]);
      const conditionCodes = this.getConditionCodesForSequence(sequenceKey, automaticConditions);

      if (!conditionCodes.length) {
        continue;
      }

      for (const sequence of sequences) {
        const tableName = `${sequence.KVEWE}${String(sequence.KOTABNR ?? '').padStart(3, '0')}`;
        const repo = this.getRepositoryForTable(tableName);
        const tableItems = await this.getTableItems(tableName, repo, tableDataCache);
        const query = this.mountQuery(
          sequence,
          conditionFieldsBySequence[this.buildSequenceCacheKey(sequence)] ?? [],
          context,
          conditionCodes,
        );
        const items = tableItems.filter(item => this.matchesQuery(item, query));

        if (items.length > 0) {
          results.set(sequenceKey, {
            condition: sequenceKey,
            sequence: sequence.KOLNR ?? '',
            table: tableName,
            register: items[0],
            records: items,
          });
          break;
        }
      }
    }
    return results;
  }

  private buildSequencesByKey(sequences: T682I[]): Record<string, T682I[]> {
    return sequences.reduce<Record<string, T682I[]>>((acc, sequence) => {
      if (!sequence.KOZGF) {
        return acc;
      }

      acc[sequence.KOZGF] = [...(acc[sequence.KOZGF] ?? []), sequence];
      return acc;
    }, {});
  }

  private buildConditionFieldsBySequence(fields: T682Z[]): Record<string, T682Z[]> {
    return fields.reduce<Record<string, T682Z[]>>((acc, field) => {
      if (!field.KOZGF) {
        return acc;
      }

      const cacheKey = `${field.KOZGF}::${field.KOLNR ?? ''}`;
      acc[cacheKey] = [...(acc[cacheKey] ?? []), field];
      return acc;
    }, {});
  }

  private getConditionCodesForSequence(sequenceKey: string, conditions: T685[]): string[] {
    return conditions
      .filter(condition => condition.KOZGF === sequenceKey || condition.KSCHL === sequenceKey)
      .map(condition => condition.KSCHL);
  }

  private async getTableItems(tableName: string, repo: RepositoryBase<any>, cache: Map<string, any[]>): Promise<any[]> {
    if (!cache.has(tableName)) {
      cache.set(tableName, await firstValueFrom(repo.getAll()));
    }

    return cache.get(tableName) ?? [];
  }

  private mountQuery(
    sequence: T682I,
    fields: T682Z[],
    context: Record<string, any>,
    conditionCodes: string[],
  ): Record<string, any> {
    const query: Record<string, any> = {
      KAPPL: sequence.KAPPL,
      KSCHL: conditionCodes.length === 1 ? conditionCodes[0] : { in: conditionCodes },
      DATAB: { lte: context['DATAB'] },
      DATBI: { gte: context['DATBI'] },
    };

    const sequenceFields = [...fields].sort((left, right) => Number(left.ZAEHK ?? 0) - Number(right.ZAEHK ?? 0));

    for (const field of sequenceFields) {
      if (!field.ZIFNA) {
        continue;
      }

      const contextValue = context[field.ZIFNA];
      query[field.ZIFNA] =
        contextValue && typeof contextValue === 'object' && 'in' in contextValue
          ? { in: contextValue.in }
          : (contextValue ?? null);
    }

    return query;
  }

  private getRepositoryForTable(tableName: string): RepositoryBase<any> {
    const map: Record<string, RepositoryBase<any>> = {
      A071: this.a071Repo,
      A073: this.a073Repo,
      A155: this.a155Repo,
    };
    const repo = map[tableName];

    if (!repo) {
      throw new Error(`Tabela de condicao ${tableName} nao mapeada`);
    }

    return repo;
  }

  private matchesQuery(item: any, query: Record<string, any>): boolean {
    return Object.entries(query).every(([field, expected]) => {
      const value = item?.[field];

      if (expected && typeof expected === 'object' && 'in' in expected) {
        return (expected.in as any[]).some(candidate => this.normalizeValue(candidate) === this.normalizeValue(value));
      }

      if (expected && typeof expected === 'object' && 'lte' in expected) {
        return this.toComparable(value) <= this.toComparable(expected.lte);
      }

      if (expected && typeof expected === 'object' && 'gte' in expected) {
        return this.toComparable(value) >= this.toComparable(expected.gte);
      }

      return this.normalizeValue(value) === this.normalizeValue(expected);
    });
  }

  private buildSequenceCacheKey(sequence: T682I): string {
    return `${sequence.KOZGF}::${sequence.KOLNR ?? ''}`;
  }

  private sortSequencesByHitProbability(sequences: T682I[]): T682I[] {
    return [...sequences].sort((left, right) => Number(left.KOLNR ?? 0) - Number(right.KOLNR ?? 0));
  }

  private toComparable(value: unknown): number {
    if (value instanceof Date) {
      return value.getTime();
    }

    const date = new Date(value as any);
    if (!Number.isNaN(date.getTime())) {
      return date.getTime();
    }

    return Number(value ?? 0);
  }

  private normalizeValue(value: unknown): unknown {
    if (typeof value === 'string') {
      return value.trim();
    }

    return value ?? null;
  }
}
